import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function useAdminCheck(userId: string | undefined) {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const checkComplete = useRef<boolean>(false);
  const { toast } = useToast();

  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      if (!userId || isLoading || checkComplete.current) return;

      try {
        setIsLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from("profiles")
          .select("is_admin")
          .eq("id", userId)
          .single();

        if (error) {
          console.error("Error checking admin status:", error);
          if (isMounted) {
            setError(error.message);
            setIsAdmin(false);
          }
          return;
        }

        if (data && isMounted) {
          setIsAdmin(!!data.is_admin);
          checkComplete.current = true;
        }
      } catch (err: any) {
        console.error("Exception checking admin status:", err);
        if (isMounted) {
          setError(err.message || "An unknown error occurred");
          setIsAdmin(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Reset check status when userId changes
    if (userId) {
      checkComplete.current = false;
      checkAdminStatus();
    } else {
      setIsAdmin(false);
      checkComplete.current = false;
    }

    return () => {
      isMounted = false;
    };
  }, [userId, isLoading, toast]);

  const forceRefresh = () => {
    checkComplete.current = false;
    setIsAdmin(false);
  };

  return {
    isAdmin,
    isLoading,
    error,
    forceRefresh,
  };
}
